/**
 * Config Service Endpoint Manager
 * Manages the dynamic config service endpoint from PublicDNSManager
 * The first entry in PublicDNSManager becomes the config service endpoint
 */

const STORAGE_KEY = 'config-service-endpoint';
const ENDPOINTS_LIST_KEY = 'public-dns-endpoints-list';

// Default fallback endpoint (empty for local development using proxy)
const DEFAULT_ENDPOINT = '';

// Get stored config service endpoint from localStorage
export const getStoredConfigEndpoint = () => {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      // Handle both .url and .baseUrl property names
      return parsed.url || parsed.baseUrl || DEFAULT_ENDPOINT;
    } catch (e) {
      return stored || DEFAULT_ENDPOINT;
    }
  }
  return DEFAULT_ENDPOINT;
};

// Store config service endpoint in localStorage
export const storeConfigEndpoint = (endpoint) => {
  // Handle both url and baseUrl property names for consistency
  const endpointUrl = endpoint?.url || endpoint?.baseUrl || endpoint;

  localStorage.setItem(STORAGE_KEY, JSON.stringify({
    url: endpointUrl,
    baseUrl: endpointUrl,
    updatedAt: new Date().toISOString()
  }));
  console.log('[ConfigEndpoint] Config service endpoint stored:', endpointUrl);

  // Dispatch event for other components to react
  window.dispatchEvent(new Event('config-endpoint-updated'));
};

// Get current config service endpoint
export const getConfigEndpoint = () => {
  return getStoredConfigEndpoint();
};

// Set config service endpoint
export const setConfigEndpoint = (url) => {
  if (!url) {
    console.warn('[ConfigEndpoint] Empty URL provided');
    return;
  }
  storeConfigEndpoint(url);
};

// Clear config service endpoint (when no endpoints are available)
export const clearConfigEndpoint = () => {
  localStorage.removeItem(STORAGE_KEY);
  console.log('[ConfigEndpoint] Config service endpoint cleared');
  window.dispatchEvent(new Event('config-endpoint-updated'));
};

// Check if config service endpoint is available
export const hasConfigEndpoint = () => {
  return !!getStoredConfigEndpoint();
};

// Store endpoints list for persistence
export const storeEndpointsList = (endpoints) => {
  localStorage.setItem(ENDPOINTS_LIST_KEY, JSON.stringify({
    endpoints,
    updatedAt: new Date().toISOString()
  }));
  console.log('[ConfigEndpoint] Endpoints list stored:', endpoints.length, 'endpoints');
};

// Get stored endpoints list
export const getStoredEndpointsList = () => {
  const stored = localStorage.getItem(ENDPOINTS_LIST_KEY);
  if (stored) {
    try {
      const parsed = JSON.parse(stored);
      return parsed.endpoints || [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

// Get the first active endpoint from stored list
export const getFirstActiveEndpoint = () => {
  const endpoints = getStoredEndpointsList();
  if (endpoints && endpoints.length > 0) {
    const firstActive = endpoints.find(e => e.status === 'active');
    if (firstActive) {
      return firstActive.baseUrl || firstActive.url || firstActive;
    }
    // If no active endpoint, return first one (handle both url and baseUrl)
    const firstEndpoint = endpoints[0];
    return firstEndpoint?.baseUrl || firstEndpoint?.url || firstEndpoint || '';
  }
  return '';
};

// Initialize config endpoint from stored endpoints on module load
const init = () => {
  // First check if we have a stored config endpoint
  const storedEndpoint = getStoredConfigEndpoint();
  if (storedEndpoint) {
    console.log('[ConfigEndpoint] Initialized with stored config endpoint:', storedEndpoint);
    return;
  }
  
  // If no stored config endpoint, try to get from stored endpoints list
  const firstEndpoint = getFirstActiveEndpoint();
  if (firstEndpoint) {
    storeConfigEndpoint(firstEndpoint);
    console.log('[ConfigEndpoint] Initialized config endpoint from stored endpoints:', firstEndpoint);
  } else {
    // No config endpoint available - user needs to visit PublicDNSManager first
    console.log('[ConfigEndpoint] ⚠️ No config endpoint available!');
    console.log('[ConfigEndpoint] 💡 Please visit PublicDNSManager section and add DNS entries first');
  }
};

// Initialize on module load
init();
